import React, { useMemo, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Divider,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { AttachMoney, ShoppingCart, Receipt, Download as DownloadIcon } from '@mui/icons-material';

interface SalesDashboardProps {
  orders: any[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const SalesDashboard: React.FC<SalesDashboardProps> = ({ orders }) => {
  const theme = useTheme();
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState<number | 'all'>('all'); // 0-11 or 'all'

  // Extract available years from orders
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    years.add(new Date().getFullYear());
    orders.forEach(o => {
      let d: Date | null = null;
      if (o.createdAt) {
        if (typeof o.createdAt.toDate === 'function') d = o.createdAt.toDate();
        else if (o.createdAt.seconds) d = new Date(o.createdAt.seconds * 1000);
        else if (typeof o.createdAt === 'string' || typeof o.createdAt === 'number') d = new Date(o.createdAt);
      }
      if (d) years.add(d.getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      let d: Date | null = null;
      if (o.createdAt) {
        if (typeof o.createdAt.toDate === 'function') d = o.createdAt.toDate();
        else if (o.createdAt.seconds) d = new Date(o.createdAt.seconds * 1000);
        else if (typeof o.createdAt === 'string' || typeof o.createdAt === 'number') d = new Date(o.createdAt);
      }
      if (!d) return false;
      
      const matchYear = d.getFullYear() === filterYear;
      const matchMonth = filterMonth === 'all' || d.getMonth() === filterMonth;
      return matchYear && matchMonth;
    });
  }, [orders, filterYear, filterMonth]);

  const {
    totalSales,
    totalOrders,
    avgTicket,
    salesByDate,
    topProducts,
    statusDistribution
  } = useMemo(() => {
    let sum = 0;
    let count = 0;
    const salesMap = new Map<string, { date: Date, total: number }>();
    const productMap: Record<string, number> = {};
    const statusMap: Record<string, number> = {};

    filteredOrders.forEach(order => {
      const total = Number(order.total) || 0;
      sum += total;
      count += 1;

      // Status Distribution
      const status = order.status || 'Desconocido';
      statusMap[status] = (statusMap[status] || 0) + 1;

      // Sales Over Time
      let dateObj: Date | null = null;
      if (order.createdAt) {
        if (typeof order.createdAt.toDate === 'function') {
          dateObj = order.createdAt.toDate();
        } else if (order.createdAt.seconds) {
             dateObj = new Date(order.createdAt.seconds * 1000);
        } else if (typeof order.createdAt === 'string' || typeof order.createdAt === 'number') {
          dateObj = new Date(order.createdAt);
        }
      }

      if (dateObj && !isNaN(dateObj.getTime())) {
        const dateKey = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD
        if (!salesMap.has(dateKey)) {
            salesMap.set(dateKey, { date: dateObj, total: 0 });
        }
        salesMap.get(dateKey)!.total += total;
      }

      // Top Products
      if (Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          const pName = item.name || item.productName || 'Producto desconocido';
          const qty = Number(item.quantity) || 1;
          productMap[pName] = (productMap[pName] || 0) + qty;
        });
      }
    });

    // Sort and format Sales by Date
    const salesByDateArr = Array.from(salesMap.values())
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map(item => ({
            name: item.date.toLocaleDateString('es-MX', { month: 'short', day: 'numeric' }),
            value: item.total
        }));

    // Sort Top Products
    const topProductsArr = Object.entries(productMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    // Format Status Distribution
    const statusDistributionArr = Object.entries(statusMap).map(([name, value]) => ({ name, value }));

    return {
      totalSales: sum,
      totalOrders: count,
      avgTicket: count ? sum / count : 0,
      salesByDate: salesByDateArr,
      topProducts: topProductsArr,
      statusDistribution: statusDistributionArr
    };
  }, [filteredOrders]);

  const handleDownloadReport = () => {
    if (filteredOrders.length === 0) return;

    // Build CSV content
    const headers = ['ID Pedido', 'Fecha', 'Cliente', 'Email', 'Teléfono', 'Total', 'Estado', 'Items'];
    const rows = filteredOrders.map(o => {
      let dateStr = '';
      if (o.createdAt) {
        let d: Date | null = null;
        if (typeof o.createdAt.toDate === 'function') d = o.createdAt.toDate();
        else if (o.createdAt.seconds) d = new Date(o.createdAt.seconds * 1000);
        else if (typeof o.createdAt === 'string' || typeof o.createdAt === 'number') d = new Date(o.createdAt);
        if (d) dateStr = d.toLocaleDateString('es-MX') + ' ' + d.toLocaleTimeString('es-MX');
      }

      const itemsStr = Array.isArray(o.items) 
        ? o.items.map((i: any) => `${i.quantity}x ${i.name || i.productName}`).join('; ')
        : '';

      // Escape fields for CSV
      const escape = (val: any) => `"${String(val || '').replace(/"/g, '""')}"`;

      return [
        escape(o.id),
        escape(dateStr),
        escape(o.userName || o.sender?.name || ''),
        escape(o.userEmail || o.sender?.email || ''),
        escape(o.userPhone || o.sender?.phone || ''),
        escape(o.total),
        escape(o.status),
        escape(itemsStr)
      ].join(',');
    });

    const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n'); // Add BOM for Excel UTF-8
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const monthName = filterMonth === 'all' ? 'Todo' : new Date(2000, filterMonth, 1).toLocaleDateString('es-MX', { month: 'long' });
    link.download = `Reporte_Ventas_${filterYear}_${monthName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val);
  };

  return (
    <Box sx={{ flexGrow: 1, p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>
          Análisis de Ventas
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Año</InputLabel>
            <Select
              value={filterYear}
              label="Año"
              onChange={(e) => setFilterYear(Number(e.target.value))}
              sx={{ borderRadius: '12px' }}
            >
              {availableYears.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Mes</InputLabel>
            <Select
              value={filterMonth}
              label="Mes"
              onChange={(e) => setFilterMonth(e.target.value as number | 'all')}
              sx={{ borderRadius: '12px' }}
            >
              <MenuItem value="all">Todo el año</MenuItem>
              <MenuItem value={0}>Enero</MenuItem>
              <MenuItem value={1}>Febrero</MenuItem>
              <MenuItem value={2}>Marzo</MenuItem>
              <MenuItem value={3}>Abril</MenuItem>
              <MenuItem value={4}>Mayo</MenuItem>
              <MenuItem value={5}>Junio</MenuItem>
              <MenuItem value={6}>Julio</MenuItem>
              <MenuItem value={7}>Agosto</MenuItem>
              <MenuItem value={8}>Septiembre</MenuItem>
              <MenuItem value={9}>Octubre</MenuItem>
              <MenuItem value={10}>Noviembre</MenuItem>
              <MenuItem value={11}>Diciembre</MenuItem>
            </Select>
          </FormControl>

          <Button 
            variant="contained" 
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
            sx={{ borderRadius: '50px', textTransform: 'none', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
          >
            Descargar Excel (CSV)
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AttachMoney fontSize="large" color="primary" />
                <Box>
                  <Typography color="textSecondary" variant="subtitle2">Ventas ({filterMonth === 'all' ? filterYear : new Date(filterYear, filterMonth).toLocaleDateString('es-MX', { month: 'long' })})</Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>{formatCurrency(totalSales)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <ShoppingCart fontSize="large" color="secondary" />
                <Box>
                  <Typography color="textSecondary" variant="subtitle2">Pedidos</Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>{totalOrders}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Receipt fontSize="large" color="success" />
                <Box>
                  <Typography color="textSecondary" variant="subtitle2">Ticket Promedio</Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>{formatCurrency(avgTicket)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, display: 'flex', flexDirection: 'column', height: 450, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>Ventas por Fecha</Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={salesByDate}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                <Legend />
                <Line type="monotone" dataKey="value" name="Ventas ($)" stroke="#1976d2" strokeWidth={2} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
           <Paper elevation={0} sx={{ p: 3, display: 'flex', flexDirection: 'column', height: 450, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>Estado de Pedidos</Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  nameKey="name"
                  label
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36}/>
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, display: 'flex', flexDirection: 'column', height: 450, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>Top 10 Productos Más Vendidos</Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topProducts} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} tick={{fontSize: 12}} />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" name="Unidades Vendidas" fill="#82ca9d" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesDashboard;
